<template>
  <div class="max-w-7xl mx-auto space-y-6">
    <!-- Header -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div class="flex items-center justify-between mb-6">
        <div class="flex items-center gap-3">
          <div class="p-2 bg-blue-100 rounded-lg">
            <FileText class="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h1 class="text-2xl font-bold text-gray-900">Account Reconciliation</h1>
            <p class="text-sm text-gray-600">Match transactions with bank statements</p>
          </div>
        </div>
      </div>

      <div class="flex items-center gap-4">
        <SearchSelect
          v-model="selectedAccountId"
          label="Select Account"
          end-point="/clients/finance/accounting/chart-of-accounts"
          placeholder="Select Account to Reconcile"
          class="flex-1 max-w-md"
          extra3="1"
          @select="onAccountChange" />
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center min-h-[400px]">
      <RefreshCw class="w-8 h-8 animate-spin text-blue-600" />
    </div>

    <template v-else>
      <div v-if="selectedAccount">
        <!-- Account Summary -->
        <div class="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div class="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
            <div class="flex items-center gap-3 mb-2">
              <h3 class="text-sm font-medium text-blue-900">Current Balance ({{ selectedAccount?.currency_code }})</h3>
            </div>
            <p class="text-3xl font-bold text-blue-900">
              {{ selectedAccount ? formatNumber((selectedAccount.closing_balance  ?? 0) / Number(exchange_rate)) : '-' }}
            </p>
          </div>

          <div class="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border border-green-200">
            <div class="flex items-center gap-3 mb-2">
              <CheckCircle2 class="w-5 h-5 text-green-600" />
              <h3 class="text-sm font-medium text-green-900">Reconciled</h3>
            </div>
            <p class="text-3xl font-bold text-green-900">{{ reconciledTransactions.length }}</p>
            <p class="text-sm text-green-700 mt-1">
              {{ formatNumber(totalReconciledAmount) }}
            </p>
          </div>

          <div class="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-6 border border-amber-200">
            <div class="flex items-center gap-3 mb-2">
              <AlertCircle class="w-5 h-5 text-amber-600" />
              <h3 class="text-sm font-medium text-amber-900">Unreconciled</h3>
            </div>
            <p class="text-3xl font-bold text-amber-900">
              {{ unreconciledTransactions.length }}
            </p>
            <p class="text-sm text-amber-700 mt-1">transactions</p>
          </div>

          <div class="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-6 border border-slate-200">
            <div class="flex items-center gap-3 mb-2">
              <Calendar class="w-5 h-5 text-slate-600" />
              <h3 class="text-sm font-medium text-slate-900">Last Reconciled</h3>
            </div>
            <p class="text-xl font-bold text-slate-900">
              {{ selectedAccount && selectedAccount.reconciliation_date ? new Date(selectedAccount.reconciliation_date).toLocaleDateString() : 'Never' }}
            </p>
            <p class="text-sm text-slate-700 mt-1">
              {{ selectedAccount && selectedAccount.last_reconciled_balance ? `${formatNumber(selectedAccount.last_reconciled_balance)}` : 'No history' }}
            </p>
          </div>
        </div>

        <!-- Reconciliation Panel -->
        <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-6">
          <h2 class="text-lg font-semibold text-gray-900 mb-4">Reconciliation Panel</h2>
          <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div>
              <DateInput
                v-model="reconciliationDate"
                label="Reconciliation Date"
                placeholder="Select reconciliation date"
                class="w-full"/>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Statement Balance ({{ selectedAccount?.currency_code }})</label>
              <input
                type="number"
                step="0.01"
                v-model="statementBalance"
                placeholder="Enter statement balance"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Selected Total ({{ selectedAccount?.currency_code }})</label>
              <div class="px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg font-semibold text-gray-900">
                {{ formatNumber(selectedTotal / Number(exchange_rate ?? 1)) }}
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Difference ({{ selectedAccount?.currency_code }})</label>
              <div
                :class="[
                  'px-4 py-2 border rounded-lg font-semibold',
                  Math.abs(Number(difference)) < 0.01
                    ? 'bg-green-50 border-green-300 text-green-900'
                    : 'bg-red-50 border-red-300 text-red-900'
                ]"
              >
                {{ formatNumber(difference) }}
              </div>
            </div>
          </div>

          <div class="flex flex-col md:flex-row items-stretch md:items-center md:justify-between gap-4">
            <div class="flex-1">
              <button
                @click="startReconciliation"
                :disabled="!statementBalance || selectedTransactions.size === 0"
                class="w-full md:w-auto px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Check class="w-5 h-5" />
                Complete Reconciliation ({{ selectedTransactions.size }} selected)
              </button>
            </div>

            <div class="flex-1 flex md:justify-end">
              <!--<button
                @click="showLedgerForm = true"
                class="w-full md:w-auto px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <CircleFadingPlus class="w-5 h-5" />
                Add Ledger Entry
              </button>-->
            </div>
          </div>
        </div>

        <!-- Unreconciled Transactions -->
        <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mt-6">
          <div class="p-6 border-b border-gray-200">
            <h2 class="text-lg font-semibold text-gray-900">Unreconciled Transactions</h2>
            <p class="text-sm text-gray-600 mt-1">Click the circle to mark transactions as tallied</p>
            {{ unreconciledTransactions.length }} transactions found.
          </div>

          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th class="px-6 py-3 text-center text-xs font-medium text-gray-700 uppercase">Tally</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Date</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Remarks</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Reference</th>
                  <th class="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase">Debit ({{ selectedAccount?.currency_code }})</th>
                  <th class="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase">Credit ({{ selectedAccount?.currency_code }})</th>
                  <th class="px-6 py-3 text-center text-xs font-medium text-gray-700 uppercase">Running Balance ({{ selectedAccount?.currency_code }})</th>
                </tr>
              </thead>

              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-if="unreconciledTransactions.length === 0">
                  <td colspan="7" class="px-6 py-12 text-center text-gray-500">
                    <CheckCircle2 class="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p class="font-medium">All transactions reconciled</p>
                    <p class="text-sm">Great job keeping your accounts in order!</p>
                  </td>
                </tr>
                <tr
                  v-for="transaction in unreconciledTransactions"
                  :key="transaction.id"
                  :class="['hover:bg-gray-50 transition-colors', selectedTransactions.has(transaction.id) ? 'bg-blue-50' : '']"
                >
                  <td class="px-6 py-4 text-center">
                    <button @click="toggleTransactionSelection(transaction.id)">
                      <component
                        :is="selectedTransactions.has(transaction.id) ? CheckCircle2 : Circle"
                        class="w-6 h-6"
                        :class="selectedTransactions.has(transaction.id)
                          ? 'text-green-600 hover:text-green-700'
                          : 'text-gray-300 hover:text-gray-400'"
                      />
                    </button>
                  </td>
                  <td class="px-6 py-4 text-sm text-gray-900">
                    {{ new Date(transaction.date).toLocaleDateString() }}
                  </td>
                  <td class="px-6 py-4 text-sm text-gray-900">{{ transaction.remarks }}<span v-if="!isEmpty(transaction.remarks)"><br /></span>{{ transaction.record_type }}</td>
                  <td class="px-6 py-4 text-sm text-gray-600">{{ transaction.reference }}</td>
                  <td class="px-6 py-4 text-sm text-right text-green-600 font-medium">
                    {{ transaction.transaction_type == 'dr' ? `${formatNumber(transaction.amount / Number(transaction.exchange_rate))}` : '-' }}
                  </td>
                  <td class="px-6 py-4 text-sm text-right text-red-600 font-medium">
                    {{ transaction.transaction_type == 'cr' ? `${formatNumber(transaction.amount / Number(transaction.exchange_rate))}` : '-' }}
                  </td>

                  <td class="px-6 py-4 text-center text-sm text-gray-900 font-semibold"
                  :class="transaction.running_total <= 0 ? 'text-red-600' : 'text-green-600'">
                    {{ formatNumber(transaction.running_total / Number(transaction.exchange_rate)) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Reconciled Transactions -->
        <div
          v-if="reconciledTransactions.length > 0"
          class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mt-6"
        >
          <div class="p-6 border-b border-gray-200 bg-green-50">
            <h2 class="text-lg font-semibold text-green-900 flex items-center gap-2">
              <CheckCircle2 class="w-5 h-5" /> Reconciled Transactions
            </h2>
            <p class="text-sm text-green-700 mt-1">Previously matched transactions</p>
          </div>

          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th class="px-6 py-3 text-center text-xs font-medium text-gray-700 uppercase">Status</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Date</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Remarks</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Reference</th>
                  <th class="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase">Debit</th>
                  <th class="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase">Credit</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="transaction in reconciledTransactions" :key="transaction.id" class="opacity-75">
                  <td class="px-6 py-4 text-center">
                    <CheckCircle2 class="w-6 h-6 text-green-600 mx-auto" />
                  </td>
                  <td class="px-6 py-4 text-sm text-gray-900">
                    {{ new Date(transaction.date).toLocaleDateString() }}
                  </td>
                  <td class="px-6 py-4 text-sm text-gray-900">{{ transaction.remarks }}<span v-if="!isEmpty(transaction.remarks)"><br /></span>{{ transaction.record_type }}</td>
                  <td class="px-6 py-4 text-sm text-gray-600">{{ transaction.reference }}</td>
                  <td class="px-6 py-4 text-sm text-right text-red-600 font-medium">
                    {{ transaction.transaction_type == 'dr' ? `${formatNumber(transaction.amount)}` : '-' }}
                  </td>
                  <td class="px-6 py-4 text-sm text-right text-green-600 font-medium">
                    {{ transaction.transaction_type == 'cr' ? `${formatNumber(transaction.amount)}` : '-' }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </template>
    <LedgerEntriesForm
      v-if="showLedgerForm"
      end-point="/clients/finance/accounting/ledger-entries/"
      :lists="[]"
      title="Add Ledger Entry"
      :mode="'create'"
      @save="transactionUpdated"
      @close="showLedgerForm = !showLedgerForm"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { Check, CheckCircle2, RefreshCw, AlertCircle, FileText, Calendar, Circle } from 'lucide-vue-next'
import { tokenManager } from '../../../../api/utils/tokenManager'
import SearchSelect from '../../../../components/ui/form/SearchSelect.vue'
import { ChartOfAccount } from '../../../../types/models'
import { api } from '../../../../api/Api'
import { formatNumber, isEmpty, toBool } from '../../../../utils/CommonFunctions'
import MessageBoxService from '../../../../services/MessageBox'
import DateInput from '../../../../components/ui/form/DateInput.vue'
import LedgerEntriesForm from '../ledgerentries/LedgerEntriesForm.vue'

const reconciliationDate = ref<Date | null>(null)
const selectedAccount = ref<ChartOfAccount | null>(null)
const selectedAccountId = ref<number | null>(null)
const transactions = ref<any[]>([])
const unreconciledTransactions = ref<any[]>([])
const reconciledTransactions = ref<any[]>([])
const lastReconciliation = ref<any | null>(null)
const statementBalance = ref(0)
const difference = ref(0)
const loading = ref(false)
const selectedTransactions = ref<Set<string>>(new Set())

const endPoint = '/clients/finance/accounting/chart-of-accounts'

const showLedgerForm = ref(false)
const transactionUpdated = (data: any) => {
  console.log('Transaction added/updated:', data)
}
onMounted(async () => {
    let acc = tokenManager.getData()
    if (acc) {
      selectedAccount.value = acc as unknown as ChartOfAccount;
      selectedAccountId.value = Number(selectedAccount.value.id);
      await getAccountDetails(selectedAccount.value)
    }
    tokenManager.removeData();
})

/*watch(selectedAccountId, async (val) => {
  if (val) {
    const acc = accounts.value.find((a) => a.id === val)
    console.log(acc)
    if(acc) {
      selectedAccountId.value = acc.id
      await getAccountDetails(acc)
    }
    //await loadTransactions(val)
    //await loadLastReconciliation(val)
  }
})*/


watch (reconciliationDate, () => {
  if (selectedAccount.value) {
    getAccountDetails(selectedAccount.value);
  }
})

watch(statementBalance, () => {
  calculateDifference();
})

const exchange_rate = ref(1)
async function getAccountDetails(account: ChartOfAccount) {
  const response = await api.post(
      (endPoint.slice(-1) == "/"
        ? endPoint
        : endPoint + "/") + "get-reconciliation",
      {
        uid: account.uid,
        reconciliation_date: reconciliationDate.value,
      }
    );
    if (response.success == true) {
      statementBalance.value = 0;
      difference.value = 0;
      selectedAccount.value = response.data.account;
      if (selectedAccount.value && selectedAccount.value.exchange_rates.length > 0) {
        exchange_rate.value = Number(selectedAccount.value.exchange_rates[0].rate);
      } else {
        exchange_rate.value = 1;
      }
      lastReconciliation.value = response.data.reconciliation || null;
      transactions.value = response.data.transactions || [];
      unreconciledTransactions.value = transactions.value.filter((t) => toBool(t.reconciled) == false)
      reconciledTransactions.value = transactions.value.filter((t) => toBool(t.reconciled) == true)
      loading.value = false;
    }
}


const selectedTotal = ref(0);
function toggleTransactionSelection(id: string) {
  const newSet = new Set(selectedTransactions.value)
  if (newSet.has(id)) newSet.delete(id)
  else newSet.add(id)
  selectedTransactions.value = newSet
  let sel =  transactions.value
    .filter((t) => selectedTransactions.value.has(t.id))
    .reduce((sum, t) => Number(sum) + Number(t.transaction_type == 'dr' ? t.amount : -t.amount), 0);
  selectedTotal.value = sel / Number(exchange_rate.value ?? 1);
  calculateDifference();
}


const calculateDifference = () => {
  let lastReconciled = Number(selectedAccount.value?.last_reconciled_balance ?? 0);
  difference.value = (selectedTotal.value + lastReconciled) - Number(statementBalance.value);
}

function onAccountChange(item: any) {
  if (item) {
    getAccountDetails(item);
  }
}

/*const selectedTotal = computed(() =>
  transactions.value
    .filter((t) => selectedTransactions.value.has(t.id))
    .reduce((sum, t) => Number(sum) + Number(t.transaction_type == 'dr' ? t.amount : -t.amount), 0)
)*/
const totalReconciledAmount = computed(() =>
  reconciledTransactions.value.reduce((sum, t) => sum + t.credit - t.debit, 0)
)
/*const statementDifference = computed(() => {
  return statementBalance.value ? parseFloat(statementBalance.value) - selectedTotal.value : 0
})*/

async function startReconciliation() {
  if (!selectedAccount.value || !statementBalance.value) return
  if (Math.abs(difference.value) > 0.01) {
    MessageBoxService.show('The selected transactions do not match the statement balance.')
    return
  }
  const selectedTxs = transactions.value.filter((t) => selectedTransactions.value.has(t.id))
  try {
    
      const response = await api.post(
      (endPoint.slice(-1) == "/"
        ? endPoint
        : endPoint + "/") + "update-reconciliation",
      {
        chart_of_accounts_id: selectedAccount.value.id,
        transactions: selectedTxs,
        reconciliation_date: reconciliationDate.value,
        ending_amount: statementBalance.value,
        previous_reconciliation_date: selectedAccount.value.reconciliation_date || null,
        begining_amount: selectedAccount.value.last_reconciled_balance || 0,
      }
    );
    if (response.success == true) {
      selectedAccountId.value = null;
      selectedAccount.value = null;
      lastReconciliation.value = null;
      transactions.value = [];
      unreconciledTransactions.value = []
      reconciledTransactions.value = []
      loading.value = false;
      MessageBoxService.show('Reconciliation completed successfully.')
    }

   
  } catch (err) {
    console.error('Error starting reconciliation:', err)
  }
}


</script>
